import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/client';
import { usePermission } from '../context/PermissionContext';

export default function BundleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canPerformAction } = usePermission();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setBundle(await api.get(`/bundles/${id}`)); }
    catch (e) { alert(e.message); navigate('/bundles'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const isOwner = bundle?.can_edit;
  const canEdit = isOwner && canPerformAction('묶음 등록/편집');
  const canDelete = isOwner && canPerformAction('묶음 삭제');
  const isPublic = bundle?.visibility === 'public';

  const doDelete = async () => {
    if (!window.confirm('이 묶음을 삭제하시겠습니까?')) return;
    try { await api.del(`/bundles/${id}`); navigate('/bundles'); }
    catch (e) { alert(e.message); }
  };

  const doToggleVisibility = async () => {
    const next = isPublic ? 'private' : 'public';
    try {
      const r = await api.patch(`/bundles/${id}/visibility`, { visibility: next });
      setBundle(b => ({ ...b, visibility: r.visibility }));
    } catch (e) { alert(e.message); }
  };

  const doClone = async () => {
    try {
      const created = await api.post(`/bundles/${id}/clone`);
      navigate(`/bundles/${created.id}`);
    } catch (e) { alert(e.message); }
  };

  if (loading || !bundle) return <div className="card" style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>로딩 중...</div>;

  const items = Array.isArray(bundle.items) ? bundle.items : [];
  const itemsSum = items.reduce((s,it)=>s+(parseInt(it.unit_price)||0)*(parseInt(it.quantity)||1), 0);

  return (
    <div>
      {/* 헤더 */}
      <div className="card" style={{ padding:'14px 18px', marginBottom:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span className={`badge ${isPublic ? 'badge-green' : 'badge-gray'}`}>
            {isPublic ? '🌐 공개' : '🔒 비공개'}
          </span>
          <span style={{ fontSize:16, fontWeight:700 }}>{bundle.name}</span>
        </div>
        <div className="btn-group">
          <button className="btn btn-outline btn-sm" onClick={()=>navigate('/bundles')}>목록으로</button>
          <button className="btn btn-primary btn-sm" onClick={()=>navigate(`/product-estimate/new?bundle=${id}`)}>📝 견적하기</button>
          {canPerformAction('묶음 등록/편집') && <button className="btn btn-outline btn-sm" onClick={doClone}>
            {isOwner ? '🔁 복제' : '📥 내 카탈로그로 가져오기'}
          </button>}
          {canEdit && (
            <>
              <button className="btn btn-outline btn-sm" onClick={doToggleVisibility}>
                {isPublic ? '🔒 비공개로' : '🌐 공개로'}
              </button>
              <button className="btn btn-outline btn-sm" onClick={()=>navigate(`/bundles/${id}/edit`)}>✎ 수정</button>
            </>
          )}
          {canDelete && (
            <button className="btn btn-outline btn-sm" style={{ color:'#ef4444', borderColor:'#fecaca' }} onClick={doDelete}>🗑 삭제</button>
          )}
        </div>
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        {/* 기본 정보 */}
        <div className="card">
          <div className="card-title" style={{ marginBottom:10 }}>기본 정보</div>
          <div style={{ fontSize:13, lineHeight:1.9 }}>
            <div><strong style={{ width:90, display:'inline-block', color:'#64748b' }}>작성자</strong>{bundle.created_by_name}</div>
            <div><strong style={{ width:90, display:'inline-block', color:'#64748b' }}>생성일</strong>{(bundle.created_at||'').slice(0,10)}</div>
            <div><strong style={{ width:90, display:'inline-block', color:'#64748b' }}>합계 금액</strong>₩{itemsSum.toLocaleString()}</div>
            {bundle.description && <div style={{ marginTop:10, padding:'10px 12px', background:'#f8fafc', borderRadius:8, whiteSpace:'pre-wrap' }}>{bundle.description}</div>}
          </div>
        </div>

        {/* 항목 */}
        <div className="card">
          <div className="card-title" style={{ marginBottom:10 }}>구성 항목 ({items.length}개)</div>
          <div style={{overflowX:'auto'}}><table style={{ fontSize:12, width:'100%' }}>
            <thead><tr style={{ background:'#f8fafc' }}>
              <th style={{ padding:8, textAlign:'left' }}>유형</th>
              <th style={{ padding:8, textAlign:'left' }}>이름/모델</th>
              <th style={{ padding:8, textAlign:'right' }}>단가</th>
              <th style={{ padding:8, textAlign:'center' }}>수량</th>
              <th style={{ padding:8, textAlign:'right' }}>합계</th>
            </tr></thead>
            <tbody>
              {items.map((it,i) => (
                <tr key={i} style={{ borderBottom:'1px solid #f1f5f9' }}>
                  <td style={{ padding:8 }}><span className={`badge ${it.item_type==='product'?'badge-blue':it.item_type==='manual'?'badge-purple':'badge-gray'}`}>{it.item_type==='product'?'상품':it.item_type==='manual'?'수기':'부품'}</span></td>
                  <td style={{ padding:8 }}>{it.item_model || it.item_name}</td>
                  <td style={{ padding:8, textAlign:'right' }}>₩{Number(it.unit_price||0).toLocaleString()}</td>
                  <td style={{ padding:8, textAlign:'center' }}>{it.quantity||1}</td>
                  <td style={{ padding:8, textAlign:'right', fontWeight:600 }}>₩{(Number(it.unit_price||0)*Number(it.quantity||1)).toLocaleString()}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} style={{ textAlign:'center', padding:20, color:'#94a3b8' }}>항목 없음</td></tr>}
            </tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}
